<template>
  <div class="my-activity-view">
    <h2 class="user-activity-title mt-6 mb-4">
      {{ $t('MyActivity.title.label') }}
    </h2>
    <div class="tabs-wrapper mb-3">
      <div class="tabs-additional">
        <v-tabs
          ref="myActivityTabs"
          v-model="activeTab"
          slider-size="4"
          class="activity-tabs"
        >
          <v-tab
            v-for="(tab, index) in tabs"
            :key="index"
            class="text-none px-0"
            @click="selectActiveTab(tab.id)"
          >
            {{ tab.label }} ({{ tab.count }})
          </v-tab>
          <div class="tabs-divider">|</div>
          <v-tab class="text-none px-0" @click="selectHistoryTab">{{
            $t('MyActivity.history.label')
          }}</v-tab>
        </v-tabs>
      </div>
    </div>
    <div class="filter-wrapper mb-3">
      <BaseTextField
        v-model="filterText"
        class="flex-grow-1"
        hide-details
        :placeholder="$t('MyActivity.TextField.label')"
        prepend-inner-icon="$search"
      />
      <div class="control-wrapper">
        <div class="period-picker">
          <BaseSelect
            v-model="period"
            :items="filterPeriods"
            item-text="label"
            item-value="value"
            hide-details
          />
        </div>
        <BaseButton
          :id="ACTIVITY_MENU_ID"
          color="black"
          icon
          outlined
          large
          :aria-label="$t('Accessibility.compilations.menu')"
          @click.native.prevent="openActivityMenu"
        >
          <BaseSpriteIcon :icon-name="'ico-hidden-menu'"></BaseSpriteIcon>
        </BaseButton>
      </div>
    </div>
    <div v-if="isNoResult" class="no-results mb-3">
      <div class="content-pic mt-12 mb-4">
        <img height="144" width="144" :src="noResultsPic" />
      </div>

      <div class="content-title mt-4 mb-2">
        {{ placeholder.title }}
      </div>
      <div
        class="content-text mt-2 mb-4"
        v-html="placeholder.description"
      ></div>
      <BaseButton
        nuxt
        :to="linkToLibrary"
        color="primary"
        large
        class=" mt-4 mb-12"
        elevation="0"
      >
        {{ $t('MyActivity.noResults.button') }}
      </BaseButton>
    </div>
    <template v-else>
      <ActivityList
        v-if="activeTab !== 3"
        :activities="filteredBooksActivities"
      />
      <ActivityHistoryList v-else :activities="filteredActivitiesByDate" />
      <div v-if="isPDFContentShown" class="pdf-page-data">
        <div ref="pdfHeaderBlock" class="pdf-header-wrapper">
          <div class="pdf-header-block pb-4 mb-16">
            <div v-if="isOcean" class="brand-logo -ocean-app">
              <div class="logo-ocean">
                <img :src="logoOcean" alt="logo" />
              </div>
              <div class="app-name-ocean">
                <img :src="appNameOceanPic" alt="appName" />
              </div>
            </div>
            <div v-else class="brand-logo -ffa-app">
              <div class="logo-ffa">
                <img :src="logoFfa" alt="logo" />
              </div>
              <div class="app-name-ffa">
                <img :src="appNameFfaPic" alt="appName" />
              </div>
            </div>
            <div v-if="isOcean" class="site-name">{{ appNameOceanText }}</div>
            <div v-else class="site-name">{{ appNameFfaText }}</div>
          </div>
        </div>
        <div ref="pdfTitleBlock" class="pdf-title-wrapper">
          <div class="pdf-title-block mb-6">
            <div class="pdf-title-text">
              {{ $t('ApplicationMenuItem.ActivityReport.label') }}
            </div>
            <div class="pdf-title-range">{{ activityPeriod }}</div>
          </div>
        </div>
        <div ref="activityContent" class="pdf-content-block">
          <ActivityList
            v-if="activeTab !== 3"
            :img-lazy-load="false"
            :activities="filteredBooksActivities"
          />
          <ActivityHistoryList
            v-else
            :img-lazy-load="false"
            :activities="filteredActivitiesByDate"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import dayJS from '@/dayJS';
import images from '@/assets/images';

import AppStateEnum from '@/enums/AppStateEnum';
import ManagePublicationsStates from '@/enums/ManagePublicationsStatesEnum';

import AppConstantsUtil from '@/services/utils/AppConstantsUtil';

import BaseTextField from '@/components/base/BaseTextField/BaseTextField';
import BaseSelect from '@/components/base/BaseSelect/BaseSelect';
import BaseButton from '@/components/base/BaseButton/BaseButton.vue';
import ActivityHistoryList from './ActivityHistoryList.vue';
import ActivityList from './ActivityList.vue';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import BrandsEnum from '@shared/enums/BrandsEnum.mjs';
import { Book } from '@/classes/factories/library/PublicationsConstructors.js';
import PublicationsTypesEnum from '@shared/enums/PublicationsTypesEnum';

const TABS = {
  ALL: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  HISTORY: 3
};

const ACTIVITY_MENU_ID = 'activity-menu';

const APP_TITLE_FFA = 'wholereader.com';
const APP_TITLE_OCEAN = 'oceanlibrary.com';

class ActivityByBook {
  setBook(book) {
    this.book = book;
    return this;
  }
  setStartAt(start) {
    this.startAt = start;
    return this;
  }
  setEndAt(endAt) {
    this.endAt = endAt;
    return this;
  }
  setCompleted(completed) {
    this.completed = completed;
    return this;
  }
  setReadPercent(readPercent) {
    this.readPercent = readPercent;
    return this;
  }
  setReadWords(readWords) {
    this.readWords = readWords;
    return this;
  }
  setChangedAt(changedAt) {
    this.changedAt = changedAt;
    return this;
  }
  setReadeTime(readeTime) {
    this.readeTime = readeTime;
    return this;
  }
  setReadPages(readPages) {
    this.readPages = readPages;
    return this;
  }
  setReadingDaysAmount(readingDaysAmount) {
    this.readingDaysAmount = readingDaysAmount;
    return this;
  }
  build() {
    return {
      book: this.book,
      startAt: this.startAt,
      endAt: this.endAt,
      completed: this.completed,
      readPercent: this.readPercent,
      readWords: this.readWords,
      changedAt: this.changedAt,
      readeTime: this.readeTime,
      readingDaysAmount: this.readingDaysAmount,
      readPages: this.readPages
    };
  }
}

class ActivityByDate {
  setBook(book) {
    this.book = book;
    return this;
  }
  setDate(date) {
    this.date = date;
    return this;
  }
  setGoal(goal) {
    this.goal = goal;
    return this;
  }
  setReadWords(readWords) {
    this.readWords = readWords;
    return this;
  }
  setChangedAt(changedAt) {
    this.changedAt = changedAt;
    return this;
  }
  setTotalWords(totalWords) {
    this.totalWords = totalWords;
    return this;
  }
  setAchievedGoal(achievedGoal) {
    this.achievedGoal = achievedGoal;
    return this;
  }
  build() {
    return {
      book: this.book,
      date: this.date,
      goal: this.goal,
      readWords: this.readWords,
      changedAt: this.changedAt,
      totalWords: this.totalWords,
      achievedGoal: this.achievedGoal
    };
  }
}

export default {
  name: 'MyActivity',
  components: {
    BaseSpriteIcon,
    BaseTextField,
    BaseSelect,
    BaseButton,
    ActivityList,
    ActivityHistoryList
  },
  data() {
    return {
      ACTIVITY_MENU_ID,
      period: 30,
      activeTab: null,
      cachedActivitiesByBook: null,
      linkToLibrary: { name: AppStateEnum.MANAGE_PUBLICATION },
      filterText: '',
      filterPeriods: [
        { label: this.$t('MyActivity.period.oneDay'), value: 1 },
        { label: this.$t('MyActivity.period.lastDays', { days: 7 }), value: 7 },
        {
          label: this.$t('MyActivity.period.lastDays', { days: 30 }),
          value: 30
        },
        {
          label: this.$t('MyActivity.period.lastDays', { days: 90 }),
          value: 90
        },
        {
          label: this.$t('MyActivity.period.lastDays', { days: 180 }),
          value: 180
        },
        { label: this.$t('MyActivity.period.allTime'), value: Infinity }
      ],
      isPDFContentShown: false,
      logoFfa: images.ffaLogo,
      appNameFfaPic: images.ffaAppName,
      logoOcean: images.oceanLogo,
      appNameOceanPic: images.oceanAppName,
      appNameFfaText: APP_TITLE_FFA,
      appNameOceanText: APP_TITLE_OCEAN
    };
  },
  computed: {
    ...mapState('ProgressStore', ['progressSummary']),
    ...mapGetters('ProgressStore', ['getPublicationProgress']),
    ...mapGetters('ActivityStore', ['getUserActivity']),
    ...mapGetters('LibraryStore', ['getPublicationById']),
    tabs() {
      const counter = this.filteredBooksActivitiesWithoutStatus.reduce(
        (result, val) => {
          const propName = val.completed ? 'completed' : 'inProgress';
          result[propName] += 1;
          return result;
        },
        {
          inProgress: 0,
          completed: 0
        }
      );
      return [
        {
          id: TABS.ALL,
          label: this.$t('MyActivity.status.all'),
          count: this.filteredBooksActivitiesWithoutStatus.length || 0
        },
        {
          id: TABS.IN_PROGRESS,
          label: this.$t('MyActivity.status.inProgress'),
          count: counter.inProgress
        },
        {
          id: TABS.COMPLETED,
          label: this.$t('MyActivity.status.completed'),
          count: counter.completed
        }
      ];
    },
    noResultsPic() {
      return this.filterText ? images.picSearch : images.picRead;
    },
    isNoResult() {
      return (
        (this.activeTab !== TABS.HISTORY &&
          !this.filteredBooksActivities.length) ||
        (this.activeTab === TABS.HISTORY &&
          !this.filteredActivitiesByDate.length)
      );
    },
    isOcean() {
      const brand = this.$store.getters['ContextStore/brand'];
      return brand === BrandsEnum.OCEAN;
    },
    placeholder() {
      let title = this.$t('MyActivity.noResults.all.title');
      let description = this.$t('MyActivity.noResults.all.description');
      if (this.filterText) {
        title = this.$t('MyActivity.noResults.filtered.title');
        description = this.$t('MyActivity.noResults.filtered.description');
      } else if (this.activeTab === TABS.IN_PROGRESS) {
        title = this.$t('MyActivity.noResults.inProgress.title');
        description = this.$t('MyActivity.noResults.inProgress.description');
      } else if (this.activeTab === TABS.COMPLETED) {
        title = this.$t('MyActivity.noResults.completed.title');
        description = this.$t('MyActivity.noResults.complete.description');
      }
      return {
        title,
        description
      };
    },
    activityPeriod() {
      if (this.period === Infinity) {
        return this.$t('MyActivity.period.allTime');
      }

      const endDate = dayJS.get();
      const startDate = endDate.subtract(this.period, 'days');
      const formattedStartDate = startDate.format('MMM D, YYYY');
      const formattedEndDate = endDate.format('MMM D, YYYY');

      const differenceInDays = endDate.diff(startDate, 'days');
      if (differenceInDays < 2) {
        return formattedEndDate;
      }

      return `${formattedStartDate} - ${formattedEndDate}`;
    },
    activities() {
      let date = dayJS.get();
      const isValidPeriod = Number.isSafeInteger(this.period);
      if (isValidPeriod && this.period > 1) {
        date = dayJS.get().subtract(this.period, 'days');
      }
      const formattedDate = isValidPeriod
        ? date.format(AppConstantsUtil.USER_ACTIVITY_DATE_FORMAT)
        : '';

      return this.getUserActivity(formattedDate) || [];
    },
    // all | in progress | completed
    activitiesByBook() {
      if (!this.progressSummary && this.cachedActivitiesByBook) {
        return this.cachedActivitiesByBook;
      }
      const booksIdsSet = new Set();
      const booksActivityById = {};
      this.activities.forEach(activity => {
        Object.entries(activity.publications || {}).forEach(([bookId, val]) => {
          booksIdsSet.add(bookId);
          if (!booksActivityById[bookId]) {
            booksActivityById[bookId] = {
              readWords: 0,
              changedAt: 0
            };
          }
          booksActivityById[bookId].readWords += val.readWords;
          booksActivityById[bookId].changedAt =
            val.changedAt > booksActivityById[bookId].changedAt
              ? val.changedAt
              : booksActivityById[bookId].changedAt;
        });
      });

      const booksIds = [...booksIdsSet];
      const activityList = booksIds.map(id => {
        const book =
          this.getPublicationById(id) || this.createUnpublishedBook(id);

        const progress = this.getPublicationProgress(id);
        const bookActivity = booksActivityById[id];
        const activityByBook = new ActivityByBook();
        activityByBook
          .setBook(book)
          .setStartAt(progress.startAt)
          .setEndAt(progress.endAt)
          .setCompleted(progress.completed)
          .setReadPercent(progress.readPercent)
          .setReadWords(bookActivity.readWords)
          .setChangedAt(bookActivity.changedAt)
          .setReadingDaysAmount(progress.readingDaysAmount)
          .setReadeTime(
            bookActivity.readWords / AppConstantsUtil.DEFAULT_AUDIO_SPEED
          )
          .setReadPages(
            bookActivity.readWords / AppConstantsUtil.AVERAGE_PAGE_WORDS_COUNT
          );
        return activityByBook.build();
      });

      return activityList.sort((a, b) => b.changedAt - a.changedAt);
    },
    filteredBooksActivitiesWithoutStatus() {
      return this.filterBooks(this.activitiesByBook, this.filterText, 'book');
    },
    filteredBooksActivities() {
      return this.filteredBooksActivitiesWithoutStatus
        .filter(activityItem => {
          return (
            this.activeTab === 0 ||
            (this.activeTab === 1 && !activityItem.completed) ||
            (this.activeTab === 2 && activityItem.completed)
          );
        })
        .sort((a, b) => b.changedAt - a.changedAt);
    },
    // history
    activitiesByDate() {
      const activities = [];
      this.activities.forEach(activity => {
        Object.entries(activity.publications).forEach(([id, val]) => {
          const book =
            this.getPublicationById(id) || this.createUnpublishedBook(id);
          const activityByDate = new ActivityByDate();
          activityByDate
            .setBook(book)
            .setDate(activity.date)
            .setGoal(activity.goal)
            .setReadWords(val.readWords)
            .setChangedAt(val.changedAt)
            .setTotalWords(activity.totalWords)
            .setAchievedGoal(activity.achievedGoal);
          activities.push(activityByDate.build());
        });
      });
      return activities.sort((a, b) => b.changedAt - a.changedAt);
    },
    filteredActivitiesByDate() {
      return this.filterBooks(this.activitiesByDate, this.filterText, 'book');
    },
    isAndroid() {
      return this.$store.getters['ContextStore/isAndroid'];
    }
  },
  watch: {
    filterText() {
      this.recalcTabsSlider();
    },
    period() {
      this.recalcTabsSlider();
    },
    activitiesByBook(val) {
      if (this.progressSummary) {
        this.cachedActivitiesByBook = cloneDeep(val);
      }
    },
    isNoResult(newValue) {
      this.$store.commit('ActivityStore/setIsNoResult', newValue);
    }
  },
  async mounted() {
    await this.$store.dispatch('LibraryStore/initState', {
      managePublicationsState: ManagePublicationsStates.LIBRARY
    });
    this.cachedActivitiesByBook = cloneDeep(this.activitiesByBook);
  },
  methods: {
    createUnpublishedBook(id) {
      return new Book({
        id,
        name: 'Unpublished book',
        category: '',
        cover: {},
        date: '',
        difficulty: 0,
        language: '',
        level: '',
        accessStatus: '',
        type: PublicationsTypesEnum.UNPUBLISHED_DEFAULT_BOOK
      });
    },
    selectActiveTab(tab) {
      this.activeTab = tab;
    },
    selectHistoryTab() {
      this.selectActiveTab(TABS.HISTORY);
    },
    filterBooks(list, text, propName) {
      const regex = new RegExp(text, 'gi');
      const filteredResult = list.filter(
        item =>
          regex.test(item[propName]?.name) || regex.test(item[propName].author)
      );
      return filteredResult;
    },
    openActivityMenu() {
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.ACTIVITY_MENU,
        popupContext: {
          activatorSelector: `#${ACTIVITY_MENU_ID}`,
          downloadPdf: this.generatePDF.bind(this)
        }
      });
    },
    recalcTabsSlider() {
      if (typeof this.$refs.myActivityTabs?.callSlider === 'function') {
        this.$refs.myActivityTabs.callSlider();
      }
    },

    async generatePDF() {
      this.isPDFContentShown = true;

      const [jsPDFImport, html2canvasImport] = await Promise.all([
        import('jspdf'),
        import('html2canvas')
      ]);

      const [jsPDF, html2canvas] = [
        jsPDFImport.default,
        html2canvasImport.default
      ];

      const pageBottomMargin = 12;
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.setFontSize(18);

      const convertToImageData = async ref => {
        const canvas = await html2canvas(ref, { useCORS: true });
        return canvas.toDataURL('image/png');
      };

      const loadImage = src => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });
      };

      const headerImgData = await convertToImageData(this.$refs.pdfHeaderBlock);
      const titleImgData = await convertToImageData(this.$refs.pdfTitleBlock);
      const contentImgData = await convertToImageData(
        this.$refs.activityContent
      );

      const [headerImg, titleImg, contentImg] = await Promise.all([
        loadImage(headerImgData),
        loadImage(titleImgData),
        loadImage(contentImgData)
      ]);

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      let height = 0;
      let isFirstPage = true;

      const addHeader = () => {
        pdf.addImage(
          headerImgData,
          'PNG',
          0,
          0,
          pdfWidth,
          (headerImg.height * pdfWidth) / headerImg.width
        );
        height = (headerImg.height * pdfWidth) / headerImg.width;
        if (isFirstPage) {
          pdf.addImage(
            titleImgData,
            'PNG',
            0,
            height,
            pdfWidth,
            (titleImg.height * pdfWidth) / titleImg.width
          );
          height += (titleImg.height * pdfWidth) / titleImg.width;
          isFirstPage = false;
        }
      };

      addHeader();

      let remainingPdfHeight = pdfHeight - height - pageBottomMargin;
      let currentContentHeight = 0;

      while (currentContentHeight < contentImg.height) {
        let imgPartHeight = Math.min(
          remainingPdfHeight * (contentImg.width / pdfWidth),
          contentImg.height - currentContentHeight
        );

        let canvasPart = document.createElement('canvas');
        let context = canvasPart.getContext('2d');
        canvasPart.width = contentImg.width;
        canvasPart.height = imgPartHeight;

        context.drawImage(
          contentImg,
          0,
          currentContentHeight,
          contentImg.width,
          imgPartHeight,
          0,
          0,
          contentImg.width,
          imgPartHeight
        );
        let imgPartData = canvasPart.toDataURL('image/png');

        if (
          imgPartHeight <
          remainingPdfHeight * (contentImg.width / pdfWidth)
        ) {
          pdf.addImage(
            imgPartData,
            'PNG',
            0,
            height,
            pdfWidth,
            imgPartHeight * (pdfWidth / contentImg.width)
          );
          currentContentHeight += imgPartHeight;
        } else {
          pdf.addImage(
            imgPartData,
            'PNG',
            0,
            height,
            pdfWidth,
            remainingPdfHeight
          );
          pdf.addPage();
          addHeader();
          currentContentHeight += imgPartHeight;
          remainingPdfHeight = pdfHeight - height - pageBottomMargin;
        }
      }

      // this.$_savePDFHandler(pdf);
      pdf.save('report.pdf');

      this.isPDFContentShown = false;
    },
    $_savePDFHandler(pdfOutput) {
      const saveForAndroid = () => {
        const directory = File.externalRootDirectory + 'Download/';
        const fileName = `invoice-${new Date().getTime()}.pdf`;
        window.cordova.file
          .writeFile(directory, fileName, pdfOutput, true)
          .then(
            () => {
              window.cordova.plugins.fileOpener2
                .showOpenWithDialog(directory + fileName, 'application/pdf')
                .then(() => window.console.log('File opened'))
                .catch(error =>
                  window.console.log('Error opening file', error)
                );
            },
            err => {
              window.console.log('Error creating file', err);
            }
          );
      };

      switch (true) {
        case this.isAndroid:
          saveForAndroid();
          break;
        default:
          pdfOutput.save('report.pdf');
      }
    }
  }
};
</script>

<style lang="less">
@import '@/assets/styles/media.less';

.my-activity-view {
  .no-results {
    border: 1px solid #cbd5e1;
    border-radius: 16px;
    padding: 16px;
    text-align: center;

    .content-title {
      font-weight: 700;
      font-size: 24px;
      line-height: 28px;
    }

    .content-text {
      font-size: 18px;
      line-height: 26px;
      color: #64748b;
    }
  }

  .filter-wrapper {
    display: flex;
    gap: 8px;

    @media @narrow {
      flex-direction: column;
      .period-picker {
        flex-grow: 1;
        flex-basis: auto;
      }
    }

    .period-picker {
      min-width: 180px;
    }

    .control-wrapper {
      display: flex;
      gap: 8px;
      flex-basis: 0;

      button {
        border-radius: 4px;
        border: 1px solid #cbd5e1;
      }
    }
  }

  .pdf-page-data {
    position: absolute;
    top: -9999px;
    left: -9999px;
    [dir='rtl'] & {
      left: auto;
      right: -9999px;
    }
  }

  .pdf-header-wrapper {
    padding: 100px 100px 1px;
    min-width: 1195px;
  }

  .pdf-header-block {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #000;

    .brand-logo {
      display: flex;
      align-items: center;
    }

    .-ocean-app {
      .logo-ocean {
        display: flex;
        width: 34px;
        height: 28px;
      }

      .app-name-ocean {
        margin-inline-start: 0.5em;
        width: 150px;
        height: 16px;

        img {
          display: block;
          width: 150px;
          height: 16px;
        }
      }
    }

    .-ffa-app {
      .logo-ffa {
        display: flex;
        width: 40px;
        height: 40px;

        img {
          width: 100%;
          height: auto;
        }
      }

      .ico-logo-name {
        display: flex;
        width: 160px;
        height: 21px;

        img {
          width: 100%;
          height: auto;
        }
      }
    }

    .site-name {
      display: flex;
      align-items: flex-end;
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: 28px;
      letter-spacing: -0.4px;
    }
  }

  .pdf-title-wrapper {
    padding: 0 100px 1px;
    min-width: 1195px;
  }

  .pdf-title-block {
    display: flex;
    justify-content: space-between;
    white-space: nowrap;
    align-items: baseline;

    .pdf-title-text {
      font-size: 32px;
      font-style: normal;
      font-weight: 700;
      line-height: 36px;
      letter-spacing: -1.28px;
    }

    .pdf-title-range {
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: 28px;
      letter-spacing: -0.4px;
    }
  }

  .pdf-content-block {
    padding: 0 100px 100px;
    min-width: 1195px;

    .col-book {
      max-width: 297px;
    }
  }
}
</style>
